import { useEffect } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Capacitor } from '@capacitor/core';

/**
 * Apply a deployed update by reloading the web app — but ONLY when {@code safe}
 * (the player is in a lobby view, never mid-game). Now that every push to main
 * auto-deploys, a long-lived session would otherwise run stale code until the
 * user happened to refresh.
 *
 * <p>With {@code registerType: 'prompt'} a new build installs and WAITS. We poll
 * so a long session actually notices a deploy, and the moment we're in a safe
 * spot we activate it: {@code updateServiceWorker(true)} posts SKIP_WAITING → the
 * SW skips waiting + claims the page → {@code controllerchange} → reload into the
 * new build. If a deploy lands mid-game, {@code needRefresh} stays true and the
 * reload fires as soon as the player returns to a lobby view.
 *
 * <p>Native builds update through the App Store / Play, so the reload is a no-op
 * there (guarded by {@link Capacitor.isNativePlatform}).
 */
export function useAutoReload(safe: boolean): void {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      // Poll for a newer build so a long-lived session picks up deploys without
      // a manual refresh. Cheap (a cache-revalidated request for the SW script);
      // the actual reload is still gated to a safe lobby view by the effect below.
      setInterval(() => {
        void registration.update();
      }, 60_000);
    },
  });

  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    if (needRefresh && safe) void updateServiceWorker(true);
  }, [needRefresh, safe, updateServiceWorker]);
}
